import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import {
  ComposedChart,
  Line,
  Area,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import {
  TrendingUp,
  Calendar,
  BarChart3,
  AlertTriangle,
  Download,
  Search,
  Activity,
  LineChart,
  AreaChart,
  CandlestickChart,
  MoreHorizontal,
  Plus,
  ChevronDown,
  SlidersHorizontal,
  Package
} from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import CompareDropdown from '../charts/CompareDropdown';
import ExportPopup from '../charts/ExportPopup';
import FrequencyPopup from '../charts/FrequencyPopup';
import Forecast from '../charts/Forecast';
import ChartSettings from '../charts/ChartSettings';
import GraphIcon from '../../assets/images/graph_icon.png';
import { useProductChartOverview, useProductChartSeries, useProductInsights } from '../../hooks';
import { productService } from '../../services';
import { ChartSkeleton } from '../Loading';
import NewsTooltipOverlay from '../charts/NewsTooltipOverlay';
// Note: aggregateDataByFrequency is intentionally NOT imported here. The
// products `/chart/series` backend honours `tf` and returns rows at the
// requested cadence, so client-side aggregation would be redundant.
// (Materials chart still uses it as a workaround — see docs/CHART_IMPLEMENTATION.md.)
import { matchNewsToDataPoints, dataPointPeriodKey } from '../../utils/chartTransforms';
import {
  ALLOWED_TIMEFRAMES_BY_FREQUENCY,
  DEFAULT_TIMEFRAME_BY_FREQUENCY,
  MIN_DATA_POINTS,
  FREQUENCY_PERIOD_MS,
  PILL_WINDOW_MS,
} from '../../constants/charts';

// Backend `tf` resolution (products API).
//
// Verified live behaviour (signality.app, May 2026):
//   tf=1d  → daily-cadence rows with FLAT OHLC (open=high=low=close=value).
//            Smallest payload; correct for line/area/bar at any frequency.
//   tf=1w  → WEEKLY-cadence rows with real OHLC. (NB: differs from the
//            materials API where tf=1w returns daily-cadence with rolling
//            OHLC.) Required for candlesticks because tf=1d strips spread.
//   tf=1m  → monthly-cadence rows with real OHLC.
//
// We always send the frequency-derived tf and rely on `aggregateDataByFrequency`
// to do client-side bucketing into the user's chosen visual cadence. The pill
// (1m / 3m / 1y / max / …) is independent — it only sets the date window.
const TF_FROM_FREQUENCY = {
  daily: '1d',
  weekly: '1w',
  monthly: '1m',
  quarterly: '3m',
  yearly: '1y',
};

const resolveBackendTf = (frequency, chartType) => {
  const base = TF_FROM_FREQUENCY[frequency] || '1d';
  // Candlesticks need OHLC spread; tf=1d returns flat OHLC.
  if (chartType === 'candlestick' && base === '1d') return '1w';
  return base;
};

const timeFrames = [
  { id: '1h', label: '1h' },
  { id: '3h', label: '3h' },
  { id: '1d', label: '1d' },
  { id: '1w', label: '1w' },
  { id: '1m', label: '1m' },
  { id: '3m', label: '3m' },
  { id: '1y', label: '1y' },
  { id: '5y', label: '5y' },
  { id: 'max', label: 'max' },
  { id: 'custom', label: 'custom' }
];

// Custom Candlestick Component
const CandlestickBar = (props) => {
  const { payload, x, y, width, height } = props;

  if (!payload || typeof payload.open === 'undefined' || typeof payload.high === 'undefined' ||
    typeof payload.low === 'undefined' || typeof payload.close === 'undefined') {
    return null;
  }

  const { open, high, low, close } = payload;
  const isPositive = close >= open;
  const color = isPositive ? '#10B981' : '#EF4444';

  // Calculate dimensions for proper candlestick
  const candleWidth = Math.max(2, width * 0.6);
  const candleX = x + (width - candleWidth) / 2;
  const wickX = x + width / 2;

  // The chart gives us the coordinates where 'high' value should be positioned
  // We need to calculate other positions relative to that
  const priceRange = high - low;
  if (priceRange === 0) return null;

  // y represents where the 'high' value is positioned
  const highY = y;

  // Calculate positions for other values based on proportional scaling
  const pixelsPerUnit = height / priceRange;

  const lowY = highY + (priceRange * pixelsPerUnit);
  const openY = highY + ((high - open) * pixelsPerUnit);
  const closeY = highY + ((high - close) * pixelsPerUnit);

  // Body dimensions
  const bodyTop = Math.min(openY, closeY);
  const bodyBottom = Math.max(openY, closeY);
  const bodyHeight = Math.max(1, Math.abs(closeY - openY));

  return (
    <g>
      {/* Upper wick */}
      <line
        x1={wickX}
        y1={highY}
        x2={wickX}
        y2={bodyTop}
        stroke={color}
        strokeWidth={1}
      />

      {/* Lower wick */}
      <line
        x1={wickX}
        y1={bodyBottom}
        x2={wickX}
        y2={lowY}
        stroke={color}
        strokeWidth={1}
      />

      {/* Candle body */}
      <rect
        x={candleX}
        y={bodyTop}
        width={candleWidth}
        height={bodyHeight}
        fill={isPositive ? 'transparent' : color}
        stroke={color}
        strokeWidth={1}
        fillOpacity={isPositive ? 0 : 1}
      />
    </g>
  );
};

const CustomTooltip = ({ active, payload, label, chartType, comparisons = [], materialName, currency }) => {
  if (active && payload && payload.length) {
    const data = payload[0]?.payload;

    return (
      <div className="chart-tooltip">
        {chartType === 'candlestick' && data ? (
          <div>
            <div className="chart-tooltip__row">
              <span className="chart-tooltip__label">Open:</span>
              <span className="chart-tooltip__value chart-tooltip__value--open">${data.open?.toLocaleString()}</span>
            </div>
            <div className="chart-tooltip__row">
              <span className="chart-tooltip__label">High:</span>
              <span className="chart-tooltip__value chart-tooltip__value--high">${data.high?.toLocaleString()}</span>
            </div>
            <div className="chart-tooltip__row">
              <span className="chart-tooltip__label">Low:</span>
              <span className="chart-tooltip__value chart-tooltip__value--low">${data.low?.toLocaleString()}</span>
            </div>
            <div className="chart-tooltip__row">
              <span className="chart-tooltip__label">Close:</span>
              <span className="chart-tooltip__value chart-tooltip__value--close">${data.close?.toLocaleString()}</span>
            </div>
            <div className="chart-tooltip__row chart-tooltip__separator">
              <span className="chart-tooltip__label">Volume:</span>
              <span className="chart-tooltip__value chart-tooltip__value--volume">{data.volume?.toLocaleString()}</span>
            </div>
          </div>
        ) : (
          <div>
            {/* Main value with color indicator and timestamp */}
            <div className={`chart-tooltip__main-row ${comparisons.length > 0 ? 'chart-tooltip__main-row--bordered' : ''}`}>
              <div className="chart-tooltip__name-group">
                <div className="chart-tooltip__indicator" style={{ backgroundColor: '#8B5CF6' }} />
                <span className="chart-tooltip__name">
                  {materialName || 'Material'}
                </span>
              </div>
              <div className="chart-tooltip__value-group">
                <div className="chart-tooltip__value">
                  {payload[0]?.value?.toLocaleString()} {currency || ''}
                </div>
                <div className="chart-tooltip__date">
                  {data?.date ? new Date(data.date).toLocaleString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                  }).toUpperCase() : label}
                </div>
              </div>
            </div>

            {/* Show comparison values */}
            {comparisons.map((comparison, index) => {
              const compValue = data?.[`comparison_${index}_value`];
              return compValue ? (
                <div key={comparison.id} className="chart-tooltip__main-row">
                  <div className="chart-tooltip__name-group">
                    <div className="chart-tooltip__indicator" style={{ backgroundColor: comparison.color }} />
                    <span className="chart-tooltip__name">
                      {comparison.name}
                    </span>
                  </div>
                  <div className="chart-tooltip__value-group">
                    <div className="chart-tooltip__value">
                      {compValue?.toLocaleString()}€
                    </div>
                    <div className="chart-tooltip__date">
                      {data?.date ? new Date(data.date).toLocaleString('en-GB', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true
                      }).toUpperCase() : label}
                    </div>
                  </div>
                </div>
              ) : null;
            })}
          </div>
        )}
      </div>
    );
  }
  return null;
};

const formatYAxisValue = (value) => {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  } else if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}k`;
  }
  return Math.round(value).toString();
};

// Chart History Navigator Component
const ChartHistoryNavigator = ({ fullData, selectionRange, onSelectionChange, selectedTimeFrame }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(null); // 'left' or 'right'
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartRange, setDragStartRange] = useState({ start: 0, end: 0 });
  const navigatorRef = useRef(null);

  const handleMouseDown = useCallback((e, action) => {
    e.preventDefault();
    const rect = navigatorRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;

    setDragStartX(clickX);
    setDragStartRange({ ...selectionRange });

    if (action === 'drag') {
      setIsDragging(true);
    } else if (action === 'resize-left' || action === 'resize-right') {
      setIsResizing(action);
    }
  }, [selectionRange]);

  const handleMouseMove = useCallback((e) => {
    if (!navigatorRef.current || (!isDragging && !isResizing)) return;

    const rect = navigatorRef.current.getBoundingClientRect();
    const currentX = e.clientX - rect.left;
    const deltaX = currentX - dragStartX;
    const containerWidth = rect.width;
    const deltaPercent = deltaX / containerWidth;

    if (isDragging) {
      // Dragging the entire selection
      const rangeWidth = dragStartRange.end - dragStartRange.start;
      let newStart = Math.max(0, Math.min(1 - rangeWidth, dragStartRange.start + deltaPercent));
      let newEnd = newStart + rangeWidth;

      // Ensure we don't go out of bounds
      if (newEnd > 1) {
        newEnd = 1;
        newStart = newEnd - rangeWidth;
      }

      onSelectionChange({ start: newStart, end: newEnd });
    } else if (isResizing === 'resize-left') {
      // Resizing from the left edge - maintain minimum width
      const minWidth = Math.max(0.02, 20 / navigatorRef.current.offsetWidth); // 2% or 20px minimum
      const newStart = Math.max(0, Math.min(dragStartRange.end - minWidth, dragStartRange.start + deltaPercent));
      onSelectionChange({ start: newStart, end: dragStartRange.end });
    } else if (isResizing === 'resize-right') {
      // Resizing from the right edge - maintain minimum width
      const minWidth = Math.max(0.02, 20 / navigatorRef.current.offsetWidth); // 2% or 20px minimum
      const newEnd = Math.min(1, Math.max(dragStartRange.start + minWidth, dragStartRange.end + deltaPercent));
      onSelectionChange({ start: dragStartRange.start, end: newEnd });
    }
  }, [isDragging, isResizing, dragStartX, dragStartRange, onSelectionChange]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(null);
  }, []);

  React.useEffect(() => {
    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isResizing, handleMouseMove, handleMouseUp]);

  const selectionStyle = {
    left: `${selectionRange.start * 100}%`,
    width: `${Math.max(2, (selectionRange.end - selectionRange.start) * 100)}%`, // Minimum 2% width
    cursor: isDragging ? 'grabbing' : 'grab',
    transition: isDragging || isResizing ? 'none' : 'all 0.3s ease' // Smooth transition when not dragging
  };


  return (
    <div className="chart-history-navigator">

      <div
        ref={navigatorRef}
        className="chart-history-overview"
      >
        {/* Overview Chart */}
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={fullData} margin={{ top: 10, right: 20, left: 20, bottom: 20 }}>
            <Area
              type="monotone"
              dataKey="value"
              stroke="#8c63ebff"
              strokeWidth={1}
              fill="rgba(136, 65, 216, 0.3)"
              dot={false}
            />
            <XAxis
              dataKey="monthLabel"
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#6B7280', fontSize: 10 }}
              interval="preserveStartEnd"
            />
            <YAxis hide />
          </ComposedChart>
        </ResponsiveContainer>

        {/* Selection Overlay */}
        <div
          className={`chart-history-slider ${isDragging ? 'dragging' : ''}`}
          style={selectionStyle}
        >
          <div
            className="chart-history-handle left"
            onMouseDown={(e) => handleMouseDown(e, 'resize-left')}
          />

          <div
            className="chart-history-handle right"
            onMouseDown={(e) => handleMouseDown(e, 'resize-right')}
          />

          <div
            className="chart-history-drag-area"
            style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
            onMouseDown={(e) => handleMouseDown(e, 'drag')}
          />
        </div>

        {/* Dimmed overlay for non-selected areas */}
        <div style={{
          position: 'absolute',
          top: '0',
          left: '0',
          right: '0',
          bottom: '0',
          background: `linear-gradient(to right,
            rgba(17, 24, 39, 0.4) 0%,
            rgba(17, 24, 39, 0.4) ${selectionRange.start * 100}%,
            transparent ${selectionRange.start * 100}%,
            transparent ${selectionRange.end * 100}%,
            rgba(17, 24, 39, 0.4) ${selectionRange.end * 100}%,
            rgba(17, 24, 39, 0.4) 100%)`,
          pointerEvents: 'none',
          zIndex: 5
        }} />
      </div>

    </div>
  );
};

export default function ModelMainChart({ comparisons = [], onAddComparison, onRemoveComparison, maxComparisons = 3, material, currentMaterialCode, onOpenBuilder, productId = null }) {
  const [selectedTimeFrame, setSelectedTimeFrame] = useState('1y');
  const [selectedChartType, setSelectedChartType] = useState('area');
  const [selectedFrequency, setSelectedFrequency] = useState('daily');
  const [showNews, setShowNews] = useState(true);
  const [showValue, setShowValue] = useState(true);
  const [showForecast, setShowForecast] = useState(true);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [showExportPopup, setShowExportPopup] = useState(false);
  const [showForecastPopup, setShowForecastPopup] = useState(false);
  const [showSettingsPopup, setShowSettingsPopup] = useState(false);
  const [showFrequencyPopup, setShowFrequencyPopup] = useState(false);

  // News tooltip state — see MainChart.jsx for the equivalent design notes.
  // `hoverState` follows the cursor while it's on the chart; `lockedTarget`
  // snapshots the data point + cursor coords at the moment of click so the
  // overlay stops chasing the cursor once the user has picked a bar.
  const [hoverState, setHoverState] = useState({ active: false, chartX: 0, chartY: 0, index: -1, inNewsBand: false });
  const [lockedTarget, setLockedTarget] = useState(null);
  const chartWrapRef = useRef(null);

  // Pill compatibility per frequency (see src/constants/charts.js). Pills
  // failing the rule stay rendered but disabled, alongside any pill where the
  // observed data density would yield fewer than MIN_DATA_POINTS in its
  // window. See `pillStates` below.
  const allowedTimeframes = useMemo(() => {
    const allowed = ALLOWED_TIMEFRAMES_BY_FREQUENCY[selectedFrequency] || ALLOWED_TIMEFRAMES_BY_FREQUENCY.daily;
    return new Set(allowed);
  }, [selectedFrequency]);

  const handleFrequencyChange = useCallback((nextFrequency) => {
    setSelectedFrequency(nextFrequency);
    const allowed = ALLOWED_TIMEFRAMES_BY_FREQUENCY[nextFrequency] || [];
    if (!allowed.includes(selectedTimeFrame)) {
      setSelectedTimeFrame(DEFAULT_TIMEFRAME_BY_FREQUENCY[nextFrequency] || 'max');
    }
  }, [selectedTimeFrame]);

  // Fetch historical overview data from API
  const {
    data: fullHistoricalData,
    loading: overviewLoading,
    error: overviewError
  } = useProductChartOverview(productId, {
    cache: true,
    cacheTTL: 10 * 60 * 1000,
  });

  // Fetch product insights for news volume bars
  const {
    data: insightsData,
  } = useProductInsights(productId, {
    cache: true,
    cacheTTL: 5 * 60 * 1000,
  });

  // Navigator selection state (as percentage of full data). The placeholder
  // last-25% range is replaced with a real 1y window the moment the overview
  // arrives, so the highlighted pill matches what's on screen at first paint.
  const [selectionRange, setSelectionRange] = useState({ start: 0.75, end: 1.0 });
  const initialSyncDoneRef = useRef(false);

  useEffect(() => {
    if (initialSyncDoneRef.current) return;
    if (!fullHistoricalData || fullHistoricalData.length === 0) return;

    const firstTs = fullHistoricalData[0].timestamp;
    const lastTs = fullHistoricalData[fullHistoricalData.length - 1].timestamp;
    const totalSpan = lastTs - firstTs;
    if (totalSpan <= 0) return;

    const oneYearMs = 365 * 24 * 60 * 60 * 1000;
    const pct = Math.max(0.02, Math.min(1.0, oneYearMs / totalSpan));
    setSelectionRange({ start: Math.max(0, 1.0 - pct), end: 1.0 });
    initialSyncDoneRef.current = true;
  }, [fullHistoricalData]);

  // Function to determine the closest timeframe based on selection range
  const getTimeframeFromSelection = useCallback((selectionRange) => {
    if (!fullHistoricalData || !fullHistoricalData.length) return 'custom';

    const totalTimeSpan = fullHistoricalData[fullHistoricalData.length - 1].timestamp - fullHistoricalData[0].timestamp;
    const selectedTimeSpan = (selectionRange.end - selectionRange.start) * totalTimeSpan;

    // Convert to days for easier comparison
    const selectedDays = selectedTimeSpan / (24 * 60 * 60 * 1000);

    // Define timeframe thresholds (in days) with some tolerance
    const timeframes = [
      { id: '1h', days: 1 / 24, tolerance: 0.1 },
      { id: '3h', days: 3 / 24, tolerance: 0.2 },
      { id: '1d', days: 1, tolerance: 0.3 },
      { id: '1w', days: 7, tolerance: 2 },
      { id: '1m', days: 30, tolerance: 5 },
      { id: '3m', days: 90, tolerance: 10 },
      { id: '1y', days: 365, tolerance: 30 },
      { id: '5y', days: 365 * 5, tolerance: 60 },
    ];

    // Check for exact match first (max = full range)
    if (selectionRange.start === 0 && selectionRange.end === 1) {
      return 'max';
    }

    // Find closest matching timeframe
    for (const timeframe of timeframes) {
      if (Math.abs(selectedDays - timeframe.days) <= timeframe.tolerance) {
        return timeframe.id;
      }
    }

    return 'custom'; // No standard timeframe matches
  }, [fullHistoricalData]);

  // Handle selection change from navigator
  const handleSelectionChange = useCallback((newSelectionRange) => {
    setSelectionRange(newSelectionRange);

    // Update timeframe to match selection if it matches a standard timeframe
    const matchingTimeframe = getTimeframeFromSelection(newSelectionRange);
    if (matchingTimeframe !== 'custom' && matchingTimeframe !== selectedTimeFrame) {
      setSelectedTimeFrame(matchingTimeframe);
    }
  }, [getTimeframeFromSelection, selectedTimeFrame]);



  // Calculate date range based on selection
  const { startDate, endDate } = useMemo(() => {
    // If custom timeframe with custom dates selected, use those
    if (selectedTimeFrame === 'custom' && customStartDate && customEndDate) {
      return { startDate: customStartDate, endDate: customEndDate };
    }

    // If no historical data, use a reasonable default range based on selected timeframe
    if (!fullHistoricalData || !fullHistoricalData.length) {
      const today = new Date();
      let rangeStart = new Date();

      switch (selectedTimeFrame) {
        case '1h': rangeStart.setHours(today.getHours() - 1); break;
        case '3h': rangeStart.setHours(today.getHours() - 3); break;
        case '1d': rangeStart.setDate(today.getDate() - 1); break;
        case '1w': rangeStart.setDate(today.getDate() - 7); break;
        case '1m': rangeStart.setMonth(today.getMonth() - 1); break;
        case '3m': rangeStart.setMonth(today.getMonth() - 3); break;
        case '1y': rangeStart.setFullYear(today.getFullYear() - 1); break;
        case '5y': rangeStart.setFullYear(today.getFullYear() - 5); break;
        case 'max': rangeStart.setFullYear(today.getFullYear() - 10); break;
        default: rangeStart.setFullYear(today.getFullYear() - 1); break;
      }

      return { startDate: rangeStart, endDate: today };
    }

    const totalSpan = fullHistoricalData[fullHistoricalData.length - 1].timestamp - fullHistoricalData[0].timestamp;
    const startTimestamp = fullHistoricalData[0].timestamp + (selectionRange.start * totalSpan);
    const endTimestamp = fullHistoricalData[0].timestamp + (selectionRange.end * totalSpan);

    return {
      startDate: new Date(startTimestamp),
      endDate: new Date(endTimestamp)
    };
  }, [fullHistoricalData, selectionRange, selectedTimeFrame, customStartDate, customEndDate]);

  // Stable string-based cache keys avoid Date-object identity churn forcing
  // unnecessary refetches each render.
  const startDateStr = useMemo(() => startDate.toISOString().split('T')[0], [startDate]);
  const endDateStr = useMemo(() => endDate.toISOString().split('T')[0], [endDate]);
  const backendTf = resolveBackendTf(selectedFrequency, selectedChartType);

  const {
    data: chartSeriesData,
    loading: seriesLoading,
    error: seriesError
  } = useProductChartSeries(productId, {
    start: startDateStr,
    end: endDateStr,
    tf: backendTf,
  }, {
    cache: true,
    cacheTTL: 5 * 60 * 1000,
    dependencies: [productId, startDateStr, endDateStr, backendTf],
    enabled: !!productId && !!startDateStr && !!endDateStr
      && (selectedTimeFrame !== 'custom' || (customStartDate && customEndDate)),
  });

  // Fetch comparison data when comparisons exist
  const [comparisonData, setComparisonData] = useState(null);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [comparisonError, setComparisonError] = useState(null);

  // Stable join — using `comparisons` directly forces a refetch every render
  // because the array identity changes even when contents don't.
  const comparisonIds = comparisons.map(comp => comp.code || comp.id).join(',');

  useEffect(() => {
    let cancelled = false;

    const fetchComparisonData = async () => {
      const isCustomWithDates = selectedTimeFrame === 'custom' && customStartDate && customEndDate;
      if (!comparisonIds || !productId || !startDateStr || !endDateStr
          || (selectedTimeFrame === 'custom' && !isCustomWithDates)) {
        setComparisonData(null);
        return;
      }

      try {
        setComparisonLoading(true);
        setComparisonError(null);

        const response = await productService.getChartComparison(productId, {
          ids: comparisonIds,
          start: startDateStr,
          end: endDateStr,
          tf: backendTf,
        });

        if (!cancelled) setComparisonData(response.series);
      } catch (error) {
        if (!cancelled) {
          console.error('[ModelMainChart] Failed to fetch comparison data:', error);
          setComparisonError(error);
        }
      } finally {
        if (!cancelled) setComparisonLoading(false);
      }
    };

    fetchComparisonData();

    return () => { cancelled = true; };
  }, [comparisonIds, productId, startDateStr, endDateStr, backendTf, selectedTimeFrame, customStartDate, customEndDate]);

  // Process and merge chart data.
  //
  // The products `/chart/series` backend already returns rows at the cadence
  // selected via `tf` (verified — see docs/CHART_IMPLEMENTATION.md §1). So we
  // do NOT re-aggregate client-side. We only:
  //   1. surface `close` as `value` (recharts dataKey="value" usage),
  //   2. add a human X-axis label,
  //   3. merge comparison series by date,
  //   4. attach matched news per point.
  const data = useMemo(() => {
    if (!chartSeriesData || !chartSeriesData.data) return [];

    const mainData = chartSeriesData.data.map(item => ({
      ...item,
      value: item.close,
      xAxisLabel: item.date
        ? new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        : '',
    }));

    let sortedData;
    if (comparisons.length === 0 || !comparisonData) {
      sortedData = mainData;
    } else {
      const dataByDate = new Map(mainData.map(item => [item.date, { ...item }]));

      comparisons.forEach((comparison, index) => {
        const comparisonSeries = comparisonData[comparison.code];
        if (!comparisonSeries || !Array.isArray(comparisonSeries)) {
          console.warn(`[ModelMainChart] No comparison data for: ${comparison.code}`);
          return;
        }

        comparisonSeries.forEach(rawCompItem => {
          const compItem = { ...rawCompItem, value: rawCompItem.close };
          const existing = dataByDate.get(compItem.date);
          if (existing) {
            existing[`comparison_${index}_value`] = compItem.close ?? compItem.value;
            existing[`comparison_${index}_open`] = compItem.open;
            existing[`comparison_${index}_high`] = compItem.high;
            existing[`comparison_${index}_low`] = compItem.low;
            existing[`comparison_${index}_close`] = compItem.close;
          } else {
            dataByDate.set(compItem.date, {
              ...compItem,
              value: null,
              [`comparison_${index}_value`]: compItem.close ?? compItem.value,
              [`comparison_${index}_open`]: compItem.open,
              [`comparison_${index}_high`]: compItem.high,
              [`comparison_${index}_low`]: compItem.low,
              [`comparison_${index}_close`]: compItem.close,
            });
          }
        });
      });

      sortedData = Array.from(dataByDate.values()).sort((a, b) =>
        new Date(a.date).getTime() - new Date(b.date).getTime()
      );
    }

    // Attach news matches per data point. Return new objects rather than
    // mutating — the cached chartSeriesData rows are frozen by useApi when
    // frequency=daily and aggregateDataByFrequency passes them through.
    const newsMatches = matchNewsToDataPoints(insightsData, selectedFrequency);
    return sortedData.map(point => {
      const key = dataPointPeriodKey(point.date, selectedFrequency);
      const entry = newsMatches.get(key);
      return {
        ...point,
        newsVolume: entry?.volume || 0,
        newsArticles: entry?.articles || [],
        newsSubGroups: entry?.subGroups || [],
      };
    });
  }, [chartSeriesData, comparisons, comparisonData, selectedFrequency, insightsData]);

  // Per-pill enabled state. A pill is disabled when:
  //   - it isn't compatible with the current frequency, OR
  //   - selecting it would yield fewer than MIN_DATA_POINTS at the cadence
  //     we currently observe in `data`.
  // `max`, `custom`, and the currently-selected pill are always enabled.
  const pillStates = useMemo(() => {
    const states = {};
    timeFrames.forEach(({ id }) => {
      if (id === selectedTimeFrame || id === 'max' || id === 'custom') {
        states[id] = { enabled: allowedTimeframes.has(id) };
        return;
      }
      if (!allowedTimeframes.has(id)) {
        states[id] = { enabled: false, reason: 'incompatible-frequency' };
        return;
      }
      const pillMs = PILL_WINDOW_MS[id];
      const periodMs = FREQUENCY_PERIOD_MS[selectedFrequency] || FREQUENCY_PERIOD_MS.daily;
      let predicted;
      if (data.length >= 2) {
        const obsStart = new Date(data[0].date).getTime();
        const obsEnd = new Date(data[data.length - 1].date).getTime();
        const obsSpan = Math.max(1, obsEnd - obsStart);
        const density = data.length / obsSpan;
        predicted = density * pillMs;
      } else {
        predicted = pillMs / periodMs;
      }
      states[id] = predicted >= MIN_DATA_POINTS
        ? { enabled: true }
        : { enabled: false, reason: 'sparse-data' };
    });
    return states;
  }, [allowedTimeframes, selectedFrequency, selectedTimeFrame, data]);

  const customRangePointEstimate = useMemo(() => {
    if (!customStartDate || !customEndDate) return 0;
    const periodMs = FREQUENCY_PERIOD_MS[selectedFrequency] || FREQUENCY_PERIOD_MS.daily;
    const span = customEndDate.getTime() - customStartDate.getTime();
    return Math.max(0, Math.floor(span / periodMs) + 1);
  }, [customStartDate, customEndDate, selectedFrequency]);
  const customRangeApplyEnabled = !!customStartDate && !!customEndDate && customRangePointEstimate >= MIN_DATA_POINTS;

  // Price axis reserves bottom 10% (news band); news axis is scaled so the
  // tallest bar reaches exactly that 10% of the chart height.
  const { priceDomain, newsDomain } = useMemo(() => {
    if (!data.length) return { priceDomain: [0, 100], newsDomain: [0, 10] };

    let pMin = Infinity;
    let pMax = -Infinity;
    let maxNews = 0;
    data.forEach(d => {
      const v = d.value ?? d.close;
      if (v != null && isFinite(v)) {
        if (v < pMin) pMin = v;
        if (v > pMax) pMax = v;
      }
      if (d.newsVolume > maxNews) maxNews = d.newsVolume;
    });

    if (!isFinite(pMin) || !isFinite(pMax)) {
      return { priceDomain: [0, 100], newsDomain: [0, Math.max(1, maxNews) * 10] };
    }

    const priceRange = Math.max(1, pMax - pMin);
    const topPadding = priceRange * 0.1;
    const bottomReserve = priceRange / 9;

    return {
      priceDomain: [Math.floor(pMin - bottomReserve), Math.ceil(pMax + topPadding)],
      newsDomain: [0, Math.max(1, maxNews) * 10],
    };
  }, [data]);

  // Handle custom date range selection
  const handleCustomDateRange = () => {
    if (!customStartDate || !customEndDate) return;

    // If no historical data, just set the timeframe to custom and close picker
    // The date range will be used directly for series API call
    if (!fullHistoricalData || !fullHistoricalData.length) {
      setSelectedTimeFrame('custom');
      setShowDatePicker(false);
      return;
    }

    const startTime = customStartDate.getTime();
    const endTime = customEndDate.getTime();

    const firstDataTime = fullHistoricalData[0].timestamp;
    const lastDataTime = fullHistoricalData[fullHistoricalData.length - 1].timestamp;
    const totalTimeSpan = lastDataTime - firstDataTime;

    // Calculate position as percentage of total data
    const startPosition = Math.max(0, (startTime - firstDataTime) / totalTimeSpan);
    const endPosition = Math.min(1, (endTime - firstDataTime) / totalTimeSpan);

    setSelectionRange({ start: startPosition, end: endPosition });
    setSelectedTimeFrame('custom');
    setShowDatePicker(false);
  };

  // News tooltip handlers — mirror MainChart's behaviour. inNewsBand is true
  // for the bottom 10% of the plot area (matches `priceDomain`'s reserved
  // band and `NEWS_BAND_TOP_FRACTION` in NewsTooltipOverlay).
  const CHART_HEIGHT = 400;
  const CHART_MARGIN_TOP = 20;
  const CHART_MARGIN_BOTTOM = 40;

  const handleChartMouseMove = useCallback((state) => {
    if (lockedTarget) return;
    if (!state || !state.isTooltipActive) {
      setHoverState(prev => (prev.active ? { ...prev, active: false } : prev));
      return;
    }
    const plotHeight = CHART_HEIGHT - CHART_MARGIN_TOP - CHART_MARGIN_BOTTOM;
    const relY = (state.chartY - CHART_MARGIN_TOP) / plotHeight;
    setHoverState({
      active: true,
      chartX: state.chartX,
      chartY: state.chartY,
      index: state.activeTooltipIndex ?? -1,
      inNewsBand: relY >= 0.9,
    });
  }, [lockedTarget]);

  const handleChartMouseLeave = useCallback(() => {
    if (lockedTarget) return;
    setHoverState(prev => (prev.active ? { ...prev, active: false } : prev));
  }, [lockedTarget]);

  const handleChartClick = useCallback((state) => {
    if (!state || !state.isTooltipActive) return;
    const idx = state.activeTooltipIndex ?? -1;
    if (idx < 0) return;
    setLockedTarget({ index: idx, chartX: state.chartX, chartY: state.chartY });
  }, []);

  const handleOverlayLeave = useCallback(() => {
    setLockedTarget(null);
    setHoverState(prev => ({ ...prev, active: false }));
  }, []);

  const handleShowAllNews = useCallback(() => {
    setLockedTarget(null);
    setHoverState(prev => ({ ...prev, active: false }));
  }, []);

  const handleTimeFrameClick = (timeFrameId) => {
    setSelectedTimeFrame(timeFrameId);
    if (!fullHistoricalData || !fullHistoricalData.length) return;

    const firstDataPoint = fullHistoricalData[0].timestamp;
    const lastDataPoint = fullHistoricalData[fullHistoricalData.length - 1].timestamp;
    const totalTimeSpan = lastDataPoint - firstDataPoint;

    let newSelectionRange;

    if (timeFrameId === 'max') {
      newSelectionRange = { start: 0.0, end: 1.0 };
    } else {
      let timeframeDurationMs;
      switch (timeFrameId) {
        case '1h': timeframeDurationMs = 60 * 60 * 1000; break;
        case '3h': timeframeDurationMs = 3 * 60 * 60 * 1000; break;
        case '1d': timeframeDurationMs = 24 * 60 * 60 * 1000; break;
        case '1w': timeframeDurationMs = 7 * 24 * 60 * 60 * 1000; break;
        case '1m': timeframeDurationMs = 30 * 24 * 60 * 60 * 1000; break;
        case '3m': timeframeDurationMs = 90 * 24 * 60 * 60 * 1000; break;
        case '1y': timeframeDurationMs = 365 * 24 * 60 * 60 * 1000; break;
        case '5y': timeframeDurationMs = 5 * 365 * 24 * 60 * 60 * 1000; break;
        default:   timeframeDurationMs = 365 * 24 * 60 * 60 * 1000;
      }

      const percentageOfTotal = Math.max(0.02, Math.min(1.0, timeframeDurationMs / totalTimeSpan));
      // Always anchor to the most recent data point — short pills mean
      // "the last N", so excluding recent data (and its news matches) is wrong.
      newSelectionRange = {
        start: Math.max(0, 1.0 - percentageOfTotal),
        end: 1.0,
      };
    }

    setSelectionRange(newSelectionRange);
  };

  // Loading state (include comparison loading)
  const isLoading = overviewLoading || seriesLoading || comparisonLoading;
  const hasError = overviewError || seriesError || comparisonError;

  return (
    <div className="chart-module">

      {/* Error Display */}
      {hasError && (
        <div className="chart-error-banner">
          <AlertTriangle size={20} />
          <div>
            <div className="chart-error-banner__title">
              {overviewError ? 'Failed to load historical data' : 'Failed to load chart data'}
            </div>
            <div className="chart-error-banner__message">
              {overviewError?.message || seriesError?.message || 'Please try again later'}
            </div>
          </div>
        </div>
      )}

      {/* Top Toolbar */}
      <div className="chart-toolbar">
        <div className="chart-toolbar__left">
          <button
            className="chart-btn chart-btn-icon-only"
            onClick={() => setShowSettingsPopup(!showSettingsPopup)}
          >
            <SlidersHorizontal size={18} />
          </button>

          {onOpenBuilder && (
            <button
              className="chart-btn chart-btn-icon-only"
              onClick={onOpenBuilder}
              title="Open Model Builder"
            >
              <Package size={18} />
            </button>
          )}

          {/* Chart Settings Popup */}
          <ChartSettings
            isOpen={showSettingsPopup}
            onClose={() => setShowSettingsPopup(false)}
            comparisons={comparisons}
          />

          {/* Chart type icons */}
          <div className="chart-type-selector">
            {[
              { type: 'area', icon: AreaChart },
              { type: 'line', icon: LineChart },
              { type: 'bar', icon: BarChart3 },
              { type: 'candlestick', icon: CandlestickChart }
            ].map(({ type, icon: Icon }) => (
              <button
                key={type}
                onClick={() => setSelectedChartType(type)}
                className={`chart-btn chart-btn-icon-only ${selectedChartType === type ? 'active' : ''}`}
              >
                <Icon size={18} />
              </button>
            ))}
          </div>

          <div className="chart-toolbar__dropdown">
            <button
              className="chart-btn chart-btn-styled"
              onClick={() => setShowFrequencyPopup(!showFrequencyPopup)}
            >
              <span>Frequency</span>
              <ChevronDown size={16} />
            </button>

            {/* Frequency Popup positioned relative to Frequency button */}
            <FrequencyPopup
              isOpen={showFrequencyPopup}
              onClose={() => setShowFrequencyPopup(false)}
              selectedFrequency={selectedFrequency}
              onFrequencyChange={handleFrequencyChange}
            />
          </div>

          <button className="chart-btn chart-btn-styled">
            <AlertTriangle size={16} />
            <span>Setup alert</span>
          </button>

          <div className="chart-toolbar__dropdown">
            <button
              className="chart-btn chart-btn-styled"
              onClick={() => setShowExportPopup(true)}
            >
              <Download size={16} />
              <span>Export</span>
            </button>

            {/* Export Popup positioned relative to Export button */}
            <ExportPopup
              isOpen={showExportPopup}
              onClose={() => setShowExportPopup(false)}
              materialName={material?.name}
              materialDate={material?.last_price_date_display}
              materialCode={currentMaterialCode}
              startDate={startDate}
              endDate={endDate}
            />
          </div>
        </div>

        <div className="chart-toolbar__center">
          <CompareDropdown
            placeholder="Compare to..."
            onItemSelect={onAddComparison}
            maxReached={comparisons.length >= maxComparisons}
            existingComparisons={comparisons}
            currentMaterialCode={currentMaterialCode}
            useProducts={!!productId}
            currentProductId={productId}
          />
        </div>

        <div className="chart-toolbar__right">
          <div className="chart-settings-container">
            <button
              className="chart-settings-btn chart-icon-btn chart-settings-btn--elevated"
              onClick={() => setShowForecastPopup(true)}
            >
              <img src={GraphIcon} alt="Graph" className="chart-icon-image" />
            </button>

            <button className="chart-settings-btn plus-icon-btn">
              <Plus size={18} className="plus-icon-btn__icon" />
            </button>

            {/* Forecast Popup positioned relative to button */}
            <Forecast
              isOpen={showForecastPopup}
              onClose={() => setShowForecastPopup(false)}
            />
          </div>
        </div>
      </div>

      {/* Main Chart Container */}
      <div className="main-chart-container">
        {/* Chart Area */}
        <div className="chart-area glass-card-ui">
          {/* Time Frame Selector */}
          <div className="chart-timeframe-bar">
            <div className="timeframe-pills-container">
              {timeFrames.map((tf) => {
                const state = pillStates[tf.id] || { enabled: true };
                const disabled = !state.enabled;
                const title = state.reason === 'sparse-data'
                  ? `Not enough data (need at least ${MIN_DATA_POINTS} points at ${selectedFrequency} frequency)`
                  : state.reason === 'incompatible-frequency'
                    ? `Not available at ${selectedFrequency} frequency`
                    : undefined;
                return (
                  <button
                    key={tf.id}
                    onClick={() => handleTimeFrameClick(tf.id)}
                    disabled={disabled}
                    title={title}
                    className={`timeframe-pill ${selectedTimeFrame === tf.id ? 'active' : ''}`}
                    style={disabled ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
                  >
                    {tf.label}
                  </button>
                );
              })}
            </div>

            <div className="chart-toolbar__dropdown">
              <button
                onClick={() => setShowDatePicker(!showDatePicker)}
                className={`date-selector-pill ${showDatePicker ? 'active' : ''}`}
              >
                <Calendar size={16} />
                <span>
                  {selectedTimeFrame === 'custom' ? 'Custom Range' : 'Select Date Range'}
                </span>
              </button>

              {/* Date Picker Dropdown */}
              {showDatePicker && (
                <div className="custom-datepicker-wrapper">
                  <div className="custom-datepicker-field-container">
                    <label className="custom-datepicker-label">
                      Start Date
                    </label>
                    <DatePicker
                      selected={customStartDate}
                      onChange={(date) => setCustomStartDate(date)}
                      selectsStart
                      startDate={customStartDate}
                      endDate={customEndDate}
                      minDate={fullHistoricalData[0] ? new Date(fullHistoricalData[0].date) : null}
                      maxDate={fullHistoricalData[fullHistoricalData.length - 1] ? new Date(fullHistoricalData[fullHistoricalData.length - 1].date) : null}
                      dateFormat="dd/MM/yyyy"
                      placeholderText="mm/dd/yyyy"
                      className="custom-datepicker-input"
                    />
                  </div>

                  <div className="custom-datepicker-field-container">
                    <label className="custom-datepicker-label">
                      End Date
                    </label>
                    <DatePicker
                      selected={customEndDate}
                      onChange={(date) => setCustomEndDate(date)}
                      selectsEnd
                      startDate={customStartDate}
                      endDate={customEndDate}
                      minDate={customStartDate || (fullHistoricalData[0] ? new Date(fullHistoricalData[0].date) : null)}
                      maxDate={fullHistoricalData[fullHistoricalData.length - 1] ? new Date(fullHistoricalData[fullHistoricalData.length - 1].date) : null}
                      dateFormat="dd/MM/yyyy"
                      placeholderText="mm/dd/yyyy"
                      className="custom-datepicker-input"
                    />
                  </div>

                  <div className="custom-datepicker-actions">
                    <button
                      onClick={() => setShowDatePicker(false)}
                      className="datepicker-btn datepicker-btn-cancel"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleCustomDateRange}
                      disabled={!customRangeApplyEnabled}
                      title={
                        customStartDate && customEndDate && !customRangeApplyEnabled
                          ? `Range needs at least ${MIN_DATA_POINTS} ${selectedFrequency} points (selected ≈ ${customRangePointEstimate})`
                          : undefined
                      }
                      className="datepicker-btn datepicker-btn-apply"
                    >
                      Apply
                    </button>
                  </div>
                  {customStartDate && customEndDate && !customRangeApplyEnabled && (
                    <div style={{
                      fontSize: '12px',
                      color: '#9CA3AF',
                      padding: '0 4px 4px',
                      textAlign: 'right',
                    }}>
                      Range needs at least {MIN_DATA_POINTS} {selectedFrequency} points
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div
            ref={chartWrapRef}
            className="chart-area__canvas"
            style={{ position: 'relative' }}
          >
            {isLoading ? (
              <ChartSkeleton height="400px" showLegend={true} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={data}
                  margin={{ top: CHART_MARGIN_TOP, right: 10, left: 0, bottom: CHART_MARGIN_BOTTOM }}
                  onMouseMove={handleChartMouseMove}
                  onMouseLeave={handleChartMouseLeave}
                  onClick={handleChartClick}
                >
                  <defs>
                    <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#C084FC" stopOpacity={0.8} />
                      <stop offset="60%" stopColor="#8B5CF6" stopOpacity={0.2} />
                      <stop offset="100%" stopColor="#8B5CF6" stopOpacity={0} />
                    </linearGradient>
                  </defs>

                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.15)" horizontal={true} vertical={false} />

                  <XAxis
                    dataKey="xAxisLabel"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#9CA3AF', fontSize: 12 }}
                    tickMargin={10}
                    interval="preserveStartEnd"
                    minTickGap={30}
                  />

                  <YAxis
                    yAxisId="price"
                    orientation="left"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#9CA3AF', fontSize: 12 }}
                    tickFormatter={formatYAxisValue}
                    domain={priceDomain}
                    tickCount={6}
                    allowDecimals={false}
                  />

                  {/* Hidden news axis — scaled so the tallest bar fits in the
                      bottom 10% of the chart, mirroring MainChart. */}
                  <YAxis
                    yAxisId="news"
                    orientation="right"
                    hide
                    domain={newsDomain}
                    allowDataOverflow
                  />

                  <Tooltip
                    content={<CustomTooltip chartType={selectedChartType} comparisons={comparisons} materialName={material?.name} currency={material?.default_currency} />}
                    cursor={{ stroke: '#8B5CF6', strokeWidth: 1, strokeDasharray: '5 5' }}
                    animationDuration={150}
                    isAnimationActive={true}
                  />

                  {selectedChartType === 'area' && (
                    <>
                      <Area
                        yAxisId="price"
                        type="linear"
                        dataKey="value"
                        stroke="#8B5CF6"
                        strokeWidth={2}
                        fill="url(#areaGradient)"
                        dot={false}
                        activeDot={{ r: 4, fill: '#8B5CF6', stroke: '#fff', strokeWidth: 2 }}
                      />
                      {/* Render comparison areas */}
                      {comparisons.map((comparison, index) => (
                        <Area
                          key={comparison.id}
                          yAxisId="price"
                          type="linear"
                          dataKey={`comparison_${index}_value`}
                          stroke={comparison.color}
                          strokeWidth={2}
                          fill={comparison.color}
                          fillOpacity={0.1}
                          dot={false}
                          activeDot={{ r: 4, fill: comparison.color, stroke: '#fff', strokeWidth: 2 }}
                        />
                      ))}
                    </>
                  )}

                  {selectedChartType === 'line' && (
                    <>
                      <Line
                        yAxisId="price"
                        type="linear"
                        dataKey="value"
                        stroke="#8B5CF6"
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 4, fill: '#8B5CF6', stroke: '#fff', strokeWidth: 2 }}
                      />
                      {/* Render comparison lines */}
                      {comparisons.map((comparison, index) => (
                        <Line
                          key={comparison.id}
                          yAxisId="price"
                          type="linear"
                          dataKey={`comparison_${index}_value`}
                          stroke={comparison.color}
                          strokeWidth={2}
                          dot={false}
                          activeDot={{ r: 4, fill: comparison.color, stroke: '#fff', strokeWidth: 2 }}
                        />
                      ))}
                    </>
                  )}

                  {selectedChartType === 'bar' && (
                    <>
                      <Bar
                        yAxisId="price"
                        dataKey="value"
                        fill="#8B5CF6"
                        opacity={0.8}
                        radius={[2, 2, 0, 0]}
                      />
                      {/* Render comparison bars */}
                      {comparisons.map((comparison, index) => (
                        <Bar
                          key={comparison.id}
                          yAxisId="price"
                          dataKey={`comparison_${index}_value`}
                          fill={comparison.color}
                          opacity={0.6}
                          radius={[2, 2, 0, 0]}
                        />
                      ))}
                    </>
                  )}

                  {selectedChartType === 'candlestick' && (
                    <Bar
                      yAxisId="price"
                      // Range accessor: recharts treats `[low, high]` as a
                      // floating bar, so the shape function receives `y` at
                      // the high pixel and `height` = (high − low) in pixels —
                      // matching CandlestickBar's geometry assumptions.
                      dataKey={(d) => [d.low, d.high]}
                      shape={(props) => <CandlestickBar {...props} />}
                      isAnimationActive={false}
                    />
                  )}

                  {showNews && (
                    <Bar
                      yAxisId="news"
                      dataKey="newsVolume"
                      fill="#F97316"
                      opacity={0.35}
                      radius={[1, 1, 0, 0]}
                      isAnimationActive={false}
                    />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            )}

            {(() => {
              if (isLoading) return null;
              const source = lockedTarget
                || (hoverState.active && hoverState.inNewsBand ? hoverState : null);
              if (!source) return null;
              const point = data[source.index];
              if (!point || !(point.newsArticles?.length > 0)) return null;
              return (
                <NewsTooltipOverlay
                  active
                  x={source.chartX}
                  y={source.chartY}
                  point={point}
                  locked={!!lockedTarget}
                  onLeaveWhileLocked={handleOverlayLeave}
                  onShowAllNews={handleShowAllNews}
                  containerRef={chartWrapRef}
                />
              );
            })()}
          </div>
        </div>

        {/* Chart History Navigator */}
        {!overviewLoading && fullHistoricalData && fullHistoricalData.length > 0 && (
          <ChartHistoryNavigator
            fullData={fullHistoricalData}
            selectionRange={selectionRange}
            onSelectionChange={handleSelectionChange}
            selectedTimeFrame={selectedTimeFrame}
          />
        )}

        {/* Bottom Legend */}
        <div className="chart-legend-container">
          <button
            onClick={() => setShowNews(!showNews)}
            className={`chart-legend-btn ${showNews ? 'active' : ''}`}
          >
            <div className="chart-legend-dot" style={{ borderColor: '#F97316' }} />
            <span>Show News</span>
          </button>

          <div className="chart-legend-group">
            <button
              onClick={() => setShowValue(!showValue)}
              className={`chart-legend-btn ${showValue ? 'active' : ''}`}
            >
              <div className="chart-legend-dot" style={{ borderColor: '#8B5CF6' }} />
              <span>Value</span>
            </button>

            {/* Render comparison legend items */}
            {comparisons.map((comparison) => (
              <button
                key={comparison.id}
                className="chart-legend-btn active"
                onClick={() => onRemoveComparison?.(comparison.id)}
                title="Remove from chart"
              >
                <div className="chart-legend-dot" style={{ borderColor: comparison.color }} />
                <span>{comparison.name.length > 20 ? comparison.name.substring(0, 20) + '...' : comparison.name}</span>
                <span style={{ marginLeft: 4, opacity: 0.5, fontSize: '0.7rem' }}>✕</span>
              </button>
            ))}

            <button
              onClick={() => setShowForecast(!showForecast)}
              className={`chart-legend-btn ${showForecast ? 'active' : ''}`}
            >
              <div className="chart-legend-dot" style={{ borderColor: '#EAB308' }} />
              <span>Forecast</span>
            </button>
          </div>
        </div>
      </div>

    </div>
  );
}